// A15 «Bildirishnomalar va broadcast» — kengaytma ekranlar uchun module-local
// data-qatlam (A15.03/04/05/07/08/14/15/16/18). C-HOT fayllarga TEGILMAYDI
// (`lib/adapters.ts` / `i18n/*` / `App.tsx` / `screenStatus.ts` / nav) — bularning
// registratsiyasi ./REGISTER.md (supervisor qo'llaydi).
//
// REAL endpointlar (bog'landi):
//   GET  /admin/broadcasts            — kampaniyalar (delivery/approval/A-B manbai)
//   POST /admin/broadcasts            — draft yaratish (multilang muharrir + A/B)
//   POST /admin/broadcasts/:id/action — queue|cancel (four-eyes tasdiq/rad)
//   GET  /admin/push/health           — push-provayder + token holati
// DORMANT (backend hali yo'q → fail-soft bo'sh; paydo bo'lsa aynan mos):
//   GET/POST /admin/broadcast-segments · GET /admin/broadcasts/engagement ·
//   GET /admin/broadcast-unsubscribes · GET /admin/push/token-health
import { api } from '@/lib/api'
import type { FetchParams, FetchResult } from '@/components/DataTable/types'

/* ── fail-soft yordamchilar (devApi naqshi) ───────────────────────── */

export function toResult<T>(rows: T[], params: FetchParams, search?: (r: T) => string): FetchResult<T> {
  const q = params.search.trim().toLowerCase()
  const filtered = q && search ? rows.filter((r) => search(r).toLowerCase().includes(q)) : rows
  const start = params.page * params.pageSize
  return { rows: filtered.slice(start, start + params.pageSize), total: filtered.length }
}

async function safeList<T>(path: string, key: string, signal?: AbortSignal): Promise<T[]> {
  try {
    const data = await api.get<Record<string, T[]>>(path, signal ? { signal } : undefined)
    return (data?.[key] as T[] | undefined) ?? []
  } catch {
    return []
  }
}

async function safeObj<T>(path: string, key: string, fallback: T, signal?: AbortSignal): Promise<T> {
  try {
    const data = await api.get<Record<string, T>>(path, signal ? { signal } : undefined)
    return (data?.[key] as T | undefined) ?? fallback
  } catch {
    return fallback
  }
}

/* ── umumiy broadcast shakli (List/Create bilan mos) ──────────────── */

export interface BroadcastLite {
  id: number
  title: string
  body: string
  audience: string
  status: string // draft|queued|sent|cancelled|failed
  recipients: number
  created_at: number
  sent_at: number | null
  scheduled_at: number | null
}

/** Barcha kampaniyalar (REAL). Fail-soft — xato bo'lsa bo'sh ro'yxat. */
export const fetchBroadcastsRaw = (signal?: AbortSignal) =>
  safeList<BroadcastLite>('/admin/broadcasts', 'broadcasts', signal)

export interface BroadcastCreateBody {
  title?: string
  body: string
  audience: string
  scheduled_at?: number
}

/** A15.05 / A15.07 — draft yaratish (REAL, hech kimga yubormaydi). */
export const createBroadcast = (b: BroadcastCreateBody) =>
  api.post<{ id: number; status: string }>('/admin/broadcasts', b)

/** A15.08 — four-eyes tasdiq (draft→queued) yoki rad (→cancelled) (REAL). */
export const broadcastAction = (id: number, action: 'queue' | 'cancel') =>
  api.post<{ ok: boolean }>(`/admin/broadcasts/${id}/action`, { action })

/* ── A15.03 auditoriya (statik presetlar + fail-soft reach) ───────── */

export interface AudiencePreset {
  key: string
  label: string
  desc: string
}

export const AUDIENCE_PRESETS: AudiencePreset[] = [
  { key: 'all', label: 'Все пользователи', desc: 'Каждый зарегистрированный аккаунт' },
  { key: 'active', label: 'Активные (30 дней)', desc: 'Заходили за последние 30 дней' },
  { key: 'staff', label: 'Персонал', desc: 'Внутренние аккаунты (админы/модераторы)' },
]

/** Auditoriya bo'yicha taxminiy qamrov (DORMANT → {}). */
export const fetchAudienceReach = (signal?: AbortSignal) =>
  safeObj<Record<string, number>>('/admin/broadcasts/audiences', 'reach', {}, signal)

/* ── A15.04 segment konstruktori (DORMANT) ────────────────────────── */

export interface SegmentRow {
  id: number
  name: string
  audience: string
  rules: string
  count: number | null
  created_at: number
}

export const SEGMENT_FIELDS = [
  { key: 'lang', label: 'Язык' },
  { key: 'country', label: 'Страна' },
  { key: 'platform', label: 'Платформа' },
  { key: 'last_seen_days', label: 'Активность (дней назад)' },
  { key: 'is_premium', label: 'Premium' },
] as const

export const SEGMENT_OPS = [
  { key: 'eq', label: '=' },
  { key: 'ne', label: '≠' },
  { key: 'gt', label: '>' },
  { key: 'lt', label: '<' },
  { key: 'in', label: 'в списке' },
] as const

export interface SegmentRuleDraft {
  field: string
  op: string
  value: string
}

export const fetchSegments = (signal?: AbortSignal) =>
  safeList<SegmentRow>('/admin/broadcast-segments', 'segments', signal)

/** POST /admin/broadcast-segments — ishga tushganda saqlaydi (DORMANT). */
export const createSegment = (b: { name: string; audience: string; rules: SegmentRuleDraft[] }) =>
  api.post<{ id: number }>('/admin/broadcast-segments', b)

/* ── A15.15 yetkazish-qamrov + deliverability (REAL) ──────────────────
 * MyGram broadcast'lar uchun open/click event-trekingi YO'Q (e'lonlarga o'qildi-
 * belgisi yo'q). Shuning uchun soxta ochilish/bosish O'RNIGA HAQIQIY o'lchanadigan
 * ma'lumot: yuborilgan broadcast'lar oluvchilari + push-token salomatligi (bu
 * broadcast'lar umuman yetib boradimi). Hammasi real Postgres-agregat. */

export interface EngagementRowReal {
  id: number
  title: string
  recipients: number
  delivered: number
  status: string
  sent_at: number | null
}

export interface EngagementReport {
  sent_campaigns: number
  total_recipients: number
  push_tokens: number
  fresh_tokens: number
  stale_tokens: number
  dead_tokens: number
  delivery_rate: number // 0..100 (foiz)
  rows: EngagementRowReal[]
}

const EMPTY_ENGAGEMENT: EngagementReport = {
  sent_campaigns: 0,
  total_recipients: 0,
  push_tokens: 0,
  fresh_tokens: 0,
  stale_tokens: 0,
  dead_tokens: 0,
  delivery_rate: 0,
  rows: [],
}

export const fetchEngagement = (signal?: AbortSignal) =>
  safeObj<EngagementReport>('/admin/broadcast-engagement', 'engagement', EMPTY_ENGAGEMENT, signal)

/* ── A15.16 bildirishnoma opt-out'lar (REAL: notify_override/mute) ─── */

export interface UnsubRow {
  id: number
  user: string
  channel: string // push|email|inapp
  reason: string
  created_at: number
}

export function fetchUnsubscribes(params: FetchParams): Promise<FetchResult<UnsubRow>> {
  return safeList<UnsubRow>('/admin/broadcast-unsubscribes', 'unsubscribes', params.signal).then((rows) =>
    toResult(rows, params, (r) => `${r.user} ${r.channel} ${r.reason}`),
  )
}

/* ── A15.18 push tokenlari salomatligi (REAL + DORMANT detali) ────── */

export interface PushHealth {
  fcm_configured: boolean
  push_tokens: number
  by_provider: Record<string, number>
  broadcast_status: Record<string, number>
}

export const fetchPushHealth = (signal?: AbortSignal) =>
  safeObj<PushHealth>(
    '/admin/push/health',
    'health',
    { fcm_configured: false, push_tokens: 0, by_provider: {}, broadcast_status: {} },
    signal,
  )

export interface TokenHealthDetail {
  fresh: number // <30 kun ichida yangilangan
  stale: number // 30–90 kun
  expired: number // 90+ kun / o'lik
  invalid: number // provayder rad etgan
}

export const fetchTokenHealthDetail = (signal?: AbortSignal) =>
  safeObj<TokenHealthDetail | null>('/admin/push/token-health', 'detail', null, signal)

/* ── module-local RU strings (i18n HOT → keyinchalik keyга ko'chadi) ─ */

export const CAMP_STR = {
  back: 'К кампаниям',
  refresh: 'Обновить',
  save: 'Сохранить',
  saved: 'Сохранено',
  failed: 'Ошибка',
  empty: 'Нет данных',
  dormantNote: 'Данные появятся, когда включится соответствующий бэкенд-модуль',

  // A15.03 audience
  audienceTitle: 'Выбор аудитории',
  audienceNote: 'Базовые аудитории кампании. Тонкая настройка — в конструкторе сегментов.',
  reach: 'Охват',
  reachUnknown: 'нет данных',
  openSegments: 'Конструктор сегментов',

  // A15.04 segments
  segmentsTitle: 'Конструктор сегментов',
  segmentsNote: 'Условия отбора аудитории (И). Сохранённые сегменты можно переиспользовать в кампаниях.',
  segName: 'Название сегмента',
  baseAudience: 'Базовая аудитория',
  rules: 'Условия',
  addRule: 'Добавить условие',
  value: 'Значение',
  createSegment: 'Сохранить сегмент',
  savedSegments: 'Сохранённые сегменты',
  members: 'Аудитория',
  noRules: 'Добавьте хотя бы одно условие',

  // A15.05 multilang editor
  editorTitle: 'Редактор сообщения (мультиязычный)',
  editorNote: 'Черновик: текст на нескольких языках. Отправки нет — только сохранение draft.',
  language: 'Язык',
  titleField: 'Заголовок',
  bodyField: 'Текст',
  audience: 'Аудитория',
  primaryLang: 'Основной язык',
  preview: 'Предпросмотр',
  saveDraft: 'Сохранить черновик',
  draftSaved: 'Черновик сохранён',
  bodyRequired: 'Заполните текст основного языка',

  // A15.07 A/B
  abTitle: 'A/B вариант',
  abNote: 'Два варианта сообщения. Сохраняются как отдельные черновики (A и B) для сравнения.',
  variantA: 'Вариант A',
  variantB: 'Вариант B',
  split: 'Доля A / B',
  createAb: 'Создать A/B (2 черновика)',
  abCreated: 'Черновики A и B созданы',
  pastAb: 'Прошлые A/B тесты',

  // A15.08 approval (four-eyes)
  approvalTitle: 'Поток согласования (four-eyes)',
  approvalNote: 'Черновики и очередь ждут второго согласующего. Согласование → в очередь отправки.',
  awaiting: 'Ожидают согласования',
  approve: 'Согласовать',
  reject: 'Отклонить',
  approved: 'Согласовано',
  rejected: 'Отклонено',
  noPending: 'Нет кампаний на согласовании',
  fourEyesHint: 'Правило: согласующий должен отличаться от автора (усилить на бэкенде).',

  // A15.14 delivery stats
  deliveryTitle: 'Статистика доставки',
  deliveryNote: 'Сводка по отправленным кампаниям: получатели, статус, доля доставки.',
  campaigns: 'Кампаний',
  sentCount: 'Отправлено',
  recipients: 'Получатели',
  delivered: 'Доставлено',
  failedCount: 'Ошибок',
  status: 'Статус',
  sentAt: 'Отправлено (когда)',

  // A15.15 доставка и охват (реальные данные — open/click не отслеживается)
  engagementTitle: 'Доставка и охват',
  engagementNote: 'Реальные данные: получатели отправленных кампаний + здоровье push-токенов (дойдёт ли рассылка). Open/click в MyGram не отслеживается — показываем доставляемость.',
  reachTotal: 'Получателей (всего)',
  reachableNow: 'Доступны сейчас',
  deliveryRate: 'Доля доставки',
  deadTokens: 'Мёртвые токены',
  freshTokens: 'Свежие (<30д)',
  staleTokens: 'Устаревшие (30–90д)',
  pushTokensTotal: 'Push-токенов',

  // A15.16 unsubscribe (реальные opt-out'ы уведомлений)
  unsubTitle: 'Отключения уведомлений',
  unsubNote: 'Реальные opt-out по чатам: notify_override=none или временный mute. (Отдельного списка отписок от рассылок в MyGram нет.)',
  user: 'Пользователь',
  channel: 'Канал',
  reason: 'Причина',
  when: 'Когда',

  // A15.18 push token health
  tokenTitle: 'Здоровье push-токенов',
  tokenNote: 'Провайдер, регистрация токенов и их свежесть (устаревшие → чистка).',
  fcm: 'FCM отправитель',
  fcmYes: 'Настроен',
  fcmNo: 'Не настроен',
  pushTokens: 'Push-токенов',
  byProvider: 'По провайдерам',
  statusBreakdown: 'Кампании по статусу',
  fresh: 'Свежие (<30д)',
  stale: 'Устаревшие (30–90д)',
  expired: 'Мёртвые (90д+)',
  invalid: 'Отклонённые провайдером',
  freshnessDormant: 'Детализация свежести появится с бэкенд-модулем',
} as const

/** Broadcast status → StatusBadge kind. */
export const STATUS_KIND: Record<string, string> = {
  draft: 'pending',
  queued: 'review',
  sent: 'active',
  cancelled: 'cancelled',
  failed: 'rejected',
}
